import { useState, useEffect, useCallback, useRef } from 'react';
import { StorageKeys } from '@/lib/storage/keys';
import { fetchWithAuth } from '@/lib/api';
import { resolveInboxSourceLabel } from '@/components/features/inbox/mappers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type InboxItemType =
  | 'voice_memo'    // Uploaded/processed voice memo
  | 'agent_message' // AI assistant output (digest, summary, response)
  | 'flow_output'   // Result of a completed FlowJob
  | 'suggestion'    // Heuristic or smart suggestion
  | 'external';     // Future: email, Slack, Telegram

export type InboxItemStatus = 'unread' | 'read' | 'archived';

export interface InboxItemAction {
  label: string;
  variant?: 'primary' | 'secondary' | 'destructive';
  /** Pre-formed prompt sent to a new chat */
  chatPrompt?: string;
  /** Documents attached as context chips in the new chat */
  contextRefs?: Array<{ id: string; title: string; path?: string; type: string }>;
  /** Open a document directly */
  openDocumentId?: string;
  /** Custom handler key (e.g. 'file', 'dismiss', 'archive') */
  handler?: string;
}

export interface InboxItem {
  id: string;
  type: InboxItemType;
  title: string;
  body: string;
  /** Human-readable source label (e.g. 'Voice Memo Pipeline', 'FlowJob: weekly-triage', 'Telegram') */
  source: string;
  timestamp: string;
  status: InboxItemStatus;
  actions?: InboxItemAction[];
  /** For voice_memo items: filename reference */
  audioFilename?: string;
  /** For flow_output items: the flow job ID */
  flowJobId?: string;
  /** For agent_message items: linked conversation ID */
  linkedConversationId?: string;
  /** Accumulative workspace org tags (threads.tagged_organization_ids). */
  taggedOrganizationIds?: string[];
  /** Optional metadata blob (varies by type) */
  meta?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = StorageKeys.INBOX_ITEMS;
const MAX_ITEMS = 200;
const EXPIRY_DAYS = 60;

/** True if id is a DB-backed item (UUID) vs localStorage-only (inbox-*) */
function isDbId(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

/** True if id is a threads table UUID (excludes flow-* synthetic inbox ids) */
function isThreadApiId(id: string): boolean {
  return isDbId(id);
}

/** Synthetic flow-run inbox ids — persist dismiss via /api/inbox, not /api/threads */
function isFlowInboxId(id: string): boolean {
  return id.startsWith('flow-');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function loadFromStorage(): InboxItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const items: InboxItem[] = JSON.parse(raw);
    const cutoff = Date.now() - EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    return items.filter(item => new Date(item.timestamp).getTime() > cutoff);
  } catch {
    return [];
  }
}

function saveToStorage(items: InboxItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

function makeId(): string {
  return `inbox-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Convert threads.kind enum value to legacy InboxItemType for backward compatibility. */
function kindToType(kind: string): InboxItemType {
  switch (kind) {
    case 'voice':           return 'voice_memo';
    case 'agent_chat':      return 'agent_message';
    case 'flow_output':     return 'flow_output';
    case 'extension_event': return 'external';
    case 'email':           return 'external';
    case 'system':          return 'flow_output';
    default:                return 'suggestion';
  }
}

/** Convert legacy InboxItemType to threads.kind enum value for API requests. */
function typeToKind(type: string): string {
  switch (type) {
    case 'voice_memo':       return 'voice';
    case 'agent_message':    return 'agent_chat';
    case 'flow_output':      return 'flow_output';
    case 'external':         return 'extension_event';
    default:                 return 'suggestion';
  }
}

/** Convert threads.status value to InboxItemStatus enum. */
function mapThreadStatus(status: string): InboxItemStatus {
  switch (status) {
    case 'archived': return 'archived';
    case 'read':     return 'read';
    case 'cleared':  return 'read';
    default:         return 'unread';
  }
}

/** Transform /api/threads response row into InboxItem format for internal state management. */
function apiThreadToItem(raw: Record<string, unknown>): InboxItem {
  const baseMeta = (raw.metadata ?? {}) as Record<string, unknown>;
  const orgId = raw.organizationId ?? raw.organization_id;
  const kind = raw.kind != null ? String(raw.kind) : '';
  const meta: Record<string, unknown> = { ...baseMeta };
  if (kind) {
    meta.threadKind = kind;
  }
  const workspaceFromApi = raw.workspaceId ?? raw.workspace_id ?? meta.workspaceId;
  if (workspaceFromApi != null) {
    meta.workspaceId = String(workspaceFromApi);
  } else if (kind !== 'agent_chat' && orgId != null) {
    // agent_chat threads often inherit JWT default org (e.g. Hop Studios) - not a workspace scope
    meta.workspaceId = String(orgId);
  }
  const rawTagged = raw.taggedOrganizationIds ?? raw.tagged_organization_ids;
  const taggedOrganizationIds = Array.isArray(rawTagged)
    ? rawTagged.map((id) => String(id)).filter(Boolean)
    : undefined;
  if (taggedOrganizationIds && taggedOrganizationIds.length > 0) {
    meta.taggedOrganizationIds = taggedOrganizationIds;
  }
  const source = resolveInboxSourceLabel(meta.source, kind);
  return {
    id: String(raw.id),
    type: kindToType(kind),
    title: String(raw.title),
    body: String(meta.body_preview ?? ''),
    source,
    timestamp: String(raw.lastMessageAt ?? raw.last_message_at ?? raw.createdAt ?? raw.created_at ?? new Date().toISOString()),
    status: mapThreadStatus(String(raw.status ?? 'unread')),
    actions: [],
    audioFilename: meta.audio_filename as string | undefined,
    flowJobId: meta.flow_job_id as string | undefined,
    linkedConversationId:
      (meta.linked_conversation_id as string | undefined) ??
      (raw.linkedConversationId as string | undefined) ??
      (raw.linked_conversation_id as string | undefined),
    taggedOrganizationIds,
    meta,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useInbox() {
  const [items, setItems] = useState<InboxItem[]>(() => loadFromStorage());
  const [isLoading, setIsLoading] = useState(true);

  // Fetch from API on mount and when window regains focus
  const fetchFromApi = useCallback(async () => {
    try {
      // ADR-0015: agent_chat belongs in Chats, not Inbox. Exclude so triage rows
      // are not crowded out of the default limit window.
      const res = await fetchWithAuth('/api/threads?limit=100&exclude_kind=agent_chat');
      if (!res.ok) return;
      const data = await res.json();
      const apiItems: InboxItem[] = (data.threads || []).map(apiThreadToItem);
      setItems(prev => {
        const merged: InboxItem[] = [...apiItems];
        for (const p of prev) {
          if (isDbId(p.id)) continue;
          const match = apiItems.find(a =>
            a.type === p.type && a.title === p.title && a.source === p.source &&
            Math.abs(new Date(a.timestamp).getTime() - new Date(p.timestamp).getTime()) < 60_000
          );
          if (!match) merged.push(p);
        }
        return merged
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          .slice(0, MAX_ITEMS);
      });
    } catch {
      // Offline or API error — keep localStorage data
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFromApi();
    const onFocus = () => fetchFromApi();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchFromApi]);

  // Migrate localStorage-only items to DB on first successful fetch (once per session)
  const migratedRef = useRef(false);
  useEffect(() => {
    if (isLoading || migratedRef.current) return;
    const localOnly = items.filter(i => !isDbId(i.id));
    if (localOnly.length === 0) return;
    migratedRef.current = true;
    const migrate = async () => {
      for (const item of localOnly) {
        try {
          const res = await fetchWithAuth('/api/threads', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              kind: typeToKind(item.type),
              title: item.title,
              initialMessage: item.body ? { role: 'external', source: 'system', body: item.body } : undefined,
              metadata: {
                source: item.source,
                audio_filename: item.audioFilename,
                flow_job_id: item.flowJobId,
                linked_conversation_id: item.linkedConversationId,
                ...item.meta,
              },
            }),
          });
          if (res.ok) {
            const created = apiThreadToItem(await res.json());
            setItems(prev => prev.map(p => p.id === item.id ? created : p));
          }
        } catch {
          // Skip on error
        }
      }
    };
    migrate();
  }, [isLoading, items]);

  // Persist to localStorage on every change
  useEffect(() => {
    saveToStorage(items);
  }, [items]);

  // Listen to the global inbox event bus — add locally and POST to API
  useEffect(() => {
    const handle = async (e: Event) => {
      const detail = (e as CustomEvent).detail as Omit<InboxItem, 'id' | 'timestamp' | 'status'>;
      if (!detail?.title || !detail?.type) return;
      const tempId = makeId();
      const item: InboxItem = {
        id: tempId,
        timestamp: new Date().toISOString(),
        status: 'unread',
        ...detail,
      };
      setItems(prev => {
        const dupe = prev.find(
          n => n.title === item.title && n.source === item.source &&
               Date.now() - new Date(n.timestamp).getTime() < 60_000
        );
        if (dupe) return prev;
        return [item, ...prev].slice(0, MAX_ITEMS);
      });
      try {
        const res = await fetchWithAuth('/api/threads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kind: typeToKind(detail.type),
            title: detail.title,
            initialMessage: detail.body ? { role: 'external', source: 'system', body: detail.body } : undefined,
            metadata: {
              source: detail.source,
              audio_filename: detail.audioFilename,
              flow_job_id: detail.flowJobId,
              linked_conversation_id: detail.linkedConversationId,
              ...detail.meta,
            },
          }),
        });
        if (res.ok) {
          const created = apiThreadToItem(await res.json());
          setItems(prev => prev.map(p => p.id === tempId ? created : p));
        }
      } catch {
        // Keep local item
      }
    };
    window.addEventListener('flownotes-inbox', handle);
    return () => window.removeEventListener('flownotes-inbox', handle);
  }, []);

  // Listen for voice-memo-processed (client-side bridge when processing in webapp)
  useEffect(() => {
    const handle = (e: Event) => {
      const detail = (e as CustomEvent).detail as { filename?: string; title?: string; transcript?: string };
      if (!detail?.filename) return;
      const item: Omit<InboxItem, 'id' | 'timestamp' | 'status'> = {
        type: 'voice_memo',
        title: detail.title || `Voice memo — ${new Date().toLocaleDateString()}`,
        body: detail.transcript
          ? detail.transcript.slice(0, 200) + (detail.transcript.length > 200 ? '…' : '')
          : 'Tap to review and file this recording.',
        source: 'Voice Memo Pipeline',
        audioFilename: detail.filename,
        actions: [
          { label: 'File as note', handler: 'file', variant: 'primary' },
          { label: 'Send to chat', handler: 'chat', chatPrompt: `Summarize and file this voice memo: ${detail.title || detail.filename}` },
          { label: 'Dismiss', handler: 'dismiss', variant: 'destructive' },
        ],
      };
      window.dispatchEvent(new CustomEvent('flownotes-inbox', { detail: item }));
    };
    window.addEventListener('voice-memo-processed', handle);
    return () => window.removeEventListener('voice-memo-processed', handle);
  }, []);

  const unreadCount = items.filter(n => n.status === 'unread').length;

  const markUnread = useCallback((id: string) => {
    setItems(prev => prev.map(n => (n.id === id ? { ...n, status: 'unread' } : n)))
    if (isDbId(id)) {
      fetchWithAuth(`/api/threads/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'unread' }),
      }).catch(() => {})
    }
  }, [])

  const markRead = useCallback((id: string) => {
    setItems(prev => prev.map(n => n.id === id ? { ...n, status: 'read' } : n));
    if (isFlowInboxId(id)) {
      fetchWithAuth(`/api/inbox/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'read' }),
      }).catch(() => {});
    } else if (isThreadApiId(id)) {
      fetchWithAuth(`/api/threads/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'read' }),
      }).catch(() => {});
    }
  }, []);

  const markAllRead = useCallback(() => {
    setItems(prev => {
      prev.filter(i => i.status === 'unread').forEach(i => {
        if (isFlowInboxId(i.id)) {
          fetchWithAuth(`/api/inbox/${encodeURIComponent(i.id)}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'read' }),
          }).catch(() => {});
        } else if (isThreadApiId(i.id)) {
          fetchWithAuth(`/api/threads/${i.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'read' }),
          }).catch(() => {});
        }
      });
      return prev.map(n => n.status === 'unread' ? { ...n, status: 'read' } : n);
    });
  }, []);

  const archive = useCallback((id: string) => {
    setItems(prev => prev.map(n => n.id === id ? { ...n, status: 'archived' } : n));
    if (isFlowInboxId(id)) {
      fetchWithAuth(`/api/inbox/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'archived' }),
      }).catch(() => {});
    } else if (isThreadApiId(id)) {
      fetchWithAuth(`/api/threads/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'archived' }),
      }).catch(() => {});
    }
  }, []);

  const dismiss = useCallback((id: string) => {
    setItems(prev => prev.filter(n => n.id !== id));
    if (isFlowInboxId(id)) {
      fetchWithAuth(`/api/inbox/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'archived' }),
      }).catch(() => {});
    } else if (isThreadApiId(id)) {
      fetchWithAuth(`/api/threads/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'archived' }),
      }).catch(() => {});
    }
  }, []);

  const clearAll = useCallback(() => setItems([]), []);

  /**
   * GET /api/threads/:id — ensures metadata.linked_conversation_id (lazy link). Updates local items.
   * Returns linked conversation UUID or null.
   */
  const hydrateThread = useCallback(async (threadId: string): Promise<string | null> => {
    if (!isThreadApiId(threadId)) return null;
    try {
      const res = await fetchWithAuth(`/api/threads/${threadId}`);
      if (!res.ok) return null;
      const data = (await res.json()) as { thread?: Record<string, unknown> };
      const raw = data.thread;
      if (!raw) return null;
      const updated = apiThreadToItem(raw);
      setItems(prev => prev.map(p => (p.id === threadId ? { ...p, ...updated } : p)));
      return updated.linkedConversationId ?? null;
    } catch {
      return null;
    }
  }, []);

  /** Push an inbox item programmatically */
  const push = useCallback((item: Omit<InboxItem, 'id' | 'timestamp' | 'status'>) => {
    window.dispatchEvent(new CustomEvent('flownotes-inbox', { detail: item }));
  }, []);

  const visible = items.filter(n => n.status !== 'archived');

  return {
    items: visible,
    allItems: items,
    unreadCount,
    isLoading,
    markRead,
    markUnread,
    markAllRead,
    archive,
    dismiss,
    clearAll,
    push,
    refetch: fetchFromApi,
    hydrateThread,
  };
}

// ---------------------------------------------------------------------------
// ThreadMessage type for message fetch
// ---------------------------------------------------------------------------

export interface ThreadMessage {
  id: string;
  threadId: string;
  role: 'user' | 'assistant' | 'system' | 'external';
  source: string;
  body: string;
  attachments: Record<string, unknown>;
  metadata: Record<string, unknown>;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// useThreadMessages hook
// ---------------------------------------------------------------------------

/**
 * Fetch messages for a specific thread. Returns empty array until threadId is set.
 */
export function useThreadMessages(threadId: string | null) {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!threadId) {
      setMessages([]);
      return;
    }
    setIsLoading(true);
    try {
      const r = await fetchWithAuth(`/api/threads/${threadId}/messages`);
      const data = r.ok ? await r.json() : { messages: [] };
      setMessages(data.messages ?? []);
    } catch {
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { messages, isLoading, refetch };
}

// ---------------------------------------------------------------------------
// Convenience emitter — import anywhere to push an inbox item
// ---------------------------------------------------------------------------

export function emitInboxItem(item: Omit<InboxItem, 'id' | 'timestamp' | 'status'>) {
  window.dispatchEvent(new CustomEvent('flownotes-inbox', { detail: item }));
}
